import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import {
  FiAlertTriangle, FiCopy, FiCheck,
  FiPaperclip, FiFileText,
  FiThumbsUp, FiThumbsDown, FiEdit3, FiZap, FiBarChart2, FiSquare,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import aiLogo from '../../assets/images/M_Multicolor.svg';
import ChartBlock from '../../components/charts/ChartBlock';
import type { ChartConfig } from '../../components/charts/ChartBlock';
import ThinkingBlock from './ThinkingBlock';
import type { ChatMessage as ChatMessageType, FileAttachment } from './chat.types';
import { submitFeedback } from '../../services/feedbackService';
import { fetchToolCallDetail, fetchConversationFile } from '../../services/historyService';
import { useStatusTemplate } from '../../context/StatusTemplateContext';
import type { StatusStep } from '../../context/StatusTemplateContext';
import { useInsuredDetail } from '../../context/InsuredDetailContext';
import { usePolicyDetail } from '../../context/PolicyDetailContext';
import { useTheme } from '../../context/ThemeContext';
import { useRunDetailPanel } from '../../context/RunDetailPanelContext';
import type { RunDetailExecution } from '../../context/RunDetailPanelContext';
import { isMeaninglessToolName } from '../../utils/toolHistory';
import MacAIChatWidget from '../mac-ai-chat/MacAIChatWidget';
import { processEntityLinks, stripTemplateMarkers } from './entityLinks';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import FeedbackEditorPopover from '../../components/ui/FeedbackEditorPopover';
import ProceedQuickReplies from '../../components/ui/ProceedQuickReplies';
import { asksToProceed, parseQuickPrompt } from '../../utils/proceedPrompt';
import '../../styles/ChatMessage.css';
import '../../styles/InsuredDetail.css';

function extractToolHistoryFromContent(content: string):
  | { aiToolName: string[]; aiToolPayload: string[]; aiToolResponse: string[] }
  | null {
  const tryParse = (raw: string): any | null => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const extractEmbeddedJson = (text: string): string | null => {
    const keyIdx = text.indexOf('"type"');
    if (keyIdx === -1) return null;
    const start = text.lastIndexOf('{', keyIdx);
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (ch === '\\') {
          escaped = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) return text.slice(start, i + 1);
      }
    }
    return null;
  };

  const trimmed = (content || '').trim();
  if (!trimmed) return null;

  let parsed = tryParse(trimmed);
  if (!parsed) {
    const fenced = trimmed.match(/```json\s*([\s\S]*?)```/i)?.[1];
    if (fenced) parsed = tryParse(fenced.trim());
  }
  if (!parsed) {
    const embedded = extractEmbeddedJson(trimmed);
    if (embedded) parsed = tryParse(embedded);
  }
  if (!parsed || parsed.type !== 'tool_history' || !Array.isArray(parsed.tool_history)) {
    return null;
  }

  const names: string[] = [];
  const payloads: string[] = [];
  const responses: string[] = [];

  for (const t of parsed.tool_history) {
    names.push(String(t?.tool_name ?? 'tool'));
    payloads.push(typeof t?.tool_payload === 'string' ? t.tool_payload : JSON.stringify(t?.tool_payload ?? ''));
    responses.push(typeof t?.tool_response === 'string' ? t.tool_response : JSON.stringify(t?.tool_response ?? ''));
  }

  return names.length > 0
    ? { aiToolName: names, aiToolPayload: payloads, aiToolResponse: responses }
    : null;
}

function extractFilesFromMessageContent(content: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const lines = (content || '').split('\n');
  let inAttachedSection = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inAttachedSection) break;
      continue;
    }
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      continue;
    }
    if (!inAttachedSection) continue;

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      rows.push({ name: withUrl[1].trim(), fileUrl: withUrl[2].trim(), size: 0, type: '' });
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (!urlOnly) continue;

    const url = urlOnly[1].trim();
    const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
    rows.push({ name: decodeURIComponent(rawName), fileUrl: url, size: 0, type: '' });
  }

  return rows;
}

// Markdown links pointing at the portal artifact viewer (…/artifacts/{guid}).
// Generated files are rendered as an attachment list below the response, so
// inline links are lifted out of the text: extracted as attachments and
// replaced in the prose by their plain link text.
const ARTIFACT_LINK_RE = /\[([^\]]*)\]\((https?:\/\/[^()\s]*\/artifacts\/[^()\s]+)\)/g;

function extractArtifactLinksFromContent(content: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const re = new RegExp(ARTIFACT_LINK_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(content || '')) !== null) {
    const url = m[2].trim();
    const fallbackName = decodeURIComponent(url.split('?')[0].split('/').pop() || '') || 'Generated file';
    rows.push({ name: (m[1] || '').trim() || fallbackName, fileUrl: url, size: 0, type: '' });
  }
  return rows;
}

function stripArtifactLinks(content: string): string {
  return (content || '').replace(ARTIFACT_LINK_RE, '$1');
}

// Artifact viewer URLs (…/artifacts/{guid}) are full portal pages — open them
// in a new tab instead of nesting the whole app inside the preview modal's
// iframe. Uploaded files are plain blob SAS URLs and keep the inline preview.
function isArtifactViewerUrl(url: string): boolean {
  return /\/artifacts\//i.test(url);
}

function normalizeAttachedFilesText(content: string): string {
  const lines = (content || '').split('\n');
  const output: string[] = [];
  let inAttachedSection = false;
  let attachedHeaderAdded = false;
  let attachedFileCount = 0;

  for (const raw of lines) {
    const line = raw.trim();
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      attachedHeaderAdded = true;
      attachedFileCount = 0;
      output.push('Attached files:');
      continue;
    }
    if (!inAttachedSection) {
      output.push(raw);
      continue;
    }

    if (!line) {
      output.push(raw);
      inAttachedSection = false;
      continue;
    }

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      attachedFileCount += 1;
      output.push(`- ${withUrl[1].trim()}`);
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (urlOnly) {
      const url = urlOnly[1].trim();
      const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
      attachedFileCount += 1;
      output.push(`- ${decodeURIComponent(rawName)}`);
      continue;
    }

    // Ignore instructional lines inside attached-file section.
    continue;
  }

  if (attachedHeaderAdded && attachedFileCount === 0) {
    return output
      .filter((line, index) => !(line.trim() === 'Attached files:' && index === output.length - 1))
      .join('\n');
  }

  return output.join('\n');
}

// Configure marked once — GFM for tables/strikethrough, sync mode
marked.use({ gfm: true });

/* ── Chart Block Parser ──
   Extracts chart data from two formats:
   1) $$ChartJSDataStart$$ ... $$ChartJSDataEnd$$ — Chart.js config from backend
   2) ```chart ... ``` — Native chart JSON blocks
   Splits content into interleaved text + chart segments for rendering.
*/
interface KpiCard {
  title: string;
  value: string;
  subtitle?: string;
}

interface ContentSegment {
  type: 'text' | 'chart' | 'kpi';
  content?: string;
  chart?: ChartConfig;
  kpiCards?: KpiCard[];
}

/** Convert Chart.js config to our Recharts ChartConfig format */
function convertChartJsToConfig(chartJs: any): ChartConfig | null {
  try {
    const chartType = chartJs.type || 'bar';
    const labels: string[] = chartJs.data?.labels || [];
    const datasets: any[] = chartJs.data?.datasets || [];

    if (labels.length === 0 || datasets.length === 0) return null;

    // Determine chart type mapping
    let type: ChartConfig['type'] = 'bar';
    if (chartType === 'bar' && chartJs.options?.indexAxis === 'y') type = 'horizontalBar';
    else if (chartType === 'line') type = 'line';
    else if (chartType === 'pie') type = 'pie';
    else if (chartType === 'doughnut') type = 'donut';
    else if (chartType === 'radar' || chartType === 'polarArea') type = 'pie';

    // Build flat data array: [{name: label, key1: val, key2: val}, ...]
    const data = labels.map((label: string, i: number) => {
      const row: Record<string, any> = { name: label };
      datasets.forEach((ds: any) => {
        const key = ds.label || `value${datasets.indexOf(ds)}`;
        row[key] = ds.data?.[i] ?? 0;
      });
      return row;
    });

    const yKeys = datasets.map((ds: any, i: number) => ds.label || `value${i}`);

    // Extract colors from datasets
    const colors = datasets.flatMap((ds: any) => {
      if (Array.isArray(ds.backgroundColor)) return ds.backgroundColor;
      if (typeof ds.backgroundColor === 'string') return [ds.backgroundColor];
      if (typeof ds.borderColor === 'string') return [ds.borderColor];
      return [];
    });

    const title = chartJs.options?.plugins?.title?.text
      || chartJs.options?.title?.text
      || '';

    return {
      type,
      title,
      data,
      xKey: 'name',
      yKeys,
      ...(colors.length > 0 ? { colors } : {}),
    };
  } catch {
    return null;
  }
}

function KpiCards({ cards }: { cards: KpiCard[] }) {
  return (
    <div className="chat-msg__kpi-cards">
      {cards.map((card, i) => (
        <div
          key={i}
          className="chat-msg__kpi-card"
        >
          <div className="chat-msg__kpi-card-title">
            {card.title}
          </div>
          <div className="chat-msg__kpi-card-value">
            {card.value}
          </div>
          {card.subtitle && (
            <div className="chat-msg__kpi-card-subtitle">{card.subtitle}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function parseChartBlocks(text: string): ContentSegment[] {
  // Combined regex for both $$ChartJSDataStart$$...$$ChartJSDataEnd$$ and ```chart...```
  const chartJsRegex = /\$\$ChartJSDataStart\$\$([\s\S]*?)\$\$ChartJSDataEnd\$\$/g;
  const fencedRegex = /```chart\s*\n([\s\S]*?)```/g;

  // Collect all chart matches with their positions and types
  const matches: { index: number; length: number; json: string; format: 'chartjs' | 'native' }[] = [];

  let m: RegExpExecArray | null;
  while ((m = chartJsRegex.exec(text)) !== null) {
    matches.push({ index: m.index, length: m[0].length, json: m[1].trim(), format: 'chartjs' });
  }
  while ((m = fencedRegex.exec(text)) !== null) {
    matches.push({ index: m.index, length: m[0].length, json: m[1].trim(), format: 'native' });
  }

  if (matches.length === 0) return [{ type: 'text', content: text }];

  // Sort by position
  matches.sort((a, b) => a.index - b.index);

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    // Text before this chart block
    if (match.index > lastIndex) {
      const before = text.slice(lastIndex, match.index).trim();
      if (before) segments.push({ type: 'text', content: before });
    }

    // Parse chart data
    try {
      const parsed = JSON.parse(match.json);

      // KPI card payload — render as metric cards, not a chart
      if (parsed.type === 'kpi' && Array.isArray(parsed.cards)) {
        segments.push({ type: 'kpi', kpiCards: parsed.cards });
        lastIndex = match.index + match.length;
        continue;
      }

      let config: ChartConfig | null = null;

      if (match.format === 'chartjs') {
        config = convertChartJsToConfig(parsed);
      } else {
        // Native format — already in our ChartConfig shape
        if (parsed.data && Array.isArray(parsed.data) && parsed.data.length > 0) {
          config = parsed as ChartConfig;
        }
      }

      if (config) {
        segments.push({ type: 'chart', chart: config });
      }
    } catch {
      // Invalid JSON — skip the markers silently
    }

    lastIndex = match.index + match.length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex).trim();
    if (remaining) segments.push({ type: 'text', content: remaining });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: text }];
}

function hasChartBlocks(text: string): boolean {
  return /\$\$ChartJSDataStart\$\$/.test(text) || /```chart\s*\n/.test(text);
}

/* ── Status Template Parser ──
   Parses $RENEWAL_MANAGEMENT_STATUS_TEMPLATE_START$ ... $RENEWAL_MANAGEMENT_STATUS_TEMPLATE_END$
   and $LEAD_MANAGEMENT_STATUS_TEMPLATE_START$ ... $LEAD_MANAGEMENT_STATUS_TEMPLATE_END$
   blocks from API responses into structured status step data.
*/
interface ParsedContent {
  textBefore: string;
  textAfter: string;
  statusSteps: StatusStep[];
  templateTitle: string;
}

function parseStatusTemplate(text: string): ParsedContent | null {
  // Match both renewal and lead templates
  const startMarkers = [
    '$$RENEWAL_MANAGEMENT_STATUS_TEMPLATE_START$$',
    '$$LEAD_MANAGEMENT_STATUS_TEMPLATE_START$$',
  ];
  const endMarkers = [
    '$$RENEWAL_MANAGEMENT_STATUS_TEMPLATE_END$$',
    '$$LEAD_MANAGEMENT_STATUS_TEMPLATE_END$$',
  ];

  let startIdx = -1;
  let endIdx = -1;
  let startMarker = '';
  let endMarker = '';
  let templateTitle = 'Status';

  for (let i = 0; i < startMarkers.length; i++) {
    const sIdx = text.indexOf(startMarkers[i]);
    if (sIdx !== -1) {
      startIdx = sIdx;
      startMarker = startMarkers[i];
      endMarker = endMarkers[i];
      templateTitle = i === 0 ? 'Renewal Management Status' : 'Lead Management Status';
      break;
    }
  }

  if (startIdx === -1) return null;

  endIdx = text.indexOf(endMarker, startIdx);
  const templateBlock = endIdx !== -1
    ? text.substring(startIdx + startMarker.length, endIdx)
    : text.substring(startIdx + startMarker.length);

  const textBefore = text.substring(0, startIdx).trim();
  const rawTextAfter = endIdx !== -1
    ? text.substring(endIdx + endMarker.length)
    : '';
  // Strip serialized JSON block from textAfter before displaying
  const textAfter = rawTextAfter
    .replace(/\$\$RENEWAL_MANAGEMENT_SERIALIZED_STATUS_JSON_START\$\$[\s\S]*?\$\$RENEWAL_MANAGEMENT_SERIALIZED_STATUS_JSON_END\$\$/g, '')
    .replace(/\$\$LEAD_MANAGEMENT_SERIALIZED_STATUS_JSON_START\$\$[\s\S]*?\$\$LEAD_MANAGEMENT_SERIALIZED_STATUS_JSON_END\$\$/g, '')
    .replace(/\$\$RENEWAL_MANAGEMENT_SERIALIZED_STATUS_JSON_START[\s\S]*/g, '')
    .replace(/\$\$LEAD_MANAGEMENT_SERIALIZED_STATUS_JSON_START[\s\S]*/g, '')
    .trim();

  // Parse the template block — supports both markdown table rows and numbered lists
  const steps: StatusStep[] = [];
  const lines = templateBlock.split('\n').filter(l => l.trim());

  const resolveStatus = (indicator: string): StatusStep['status'] => {
    if (/✅|✓|done|complete|finished|success/i.test(indicator)) return 'completed';
    if (/❌|✗|fail/i.test(indicator)) return 'failed';
    if (/⏳|🔄|in.?progress|running|processing/i.test(indicator)) return 'in_progress';
    return 'pending';
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Markdown table row: | 1 | Identify Policy to Renew | ✅ |
    const tableMatch = trimmed.match(/^\|\s*(\d+)\s*\|\s*(.+?)\s*\|\s*(.+?)\s*\|/);
    if (tableMatch) {
      const stepNum = parseInt(tableMatch[1], 10);
      const desc = tableMatch[2].trim();
      const statusCell = tableMatch[3].trim();
      steps.push({ step: stepNum, description: desc, status: resolveStatus(statusCell) });
      continue;
    }

    // Numbered list: "1. Some Description - ✅"
    const numMatch = trimmed.match(/^(\d+)\.\s*(.+)/);
    if (numMatch) {
      const stepNum = parseInt(numMatch[1], 10);
      let desc = numMatch[2].trim();
      const status = resolveStatus(desc);
      // Strip status indicators from description
      desc = desc
        .replace(/\s*[-–—:]\s*(✅|✓|complete|done|finished|success|❌|✗|failed|error|fail|⏳|🔄|in.?progress|running|processing)\s*/gi, '')
        .replace(/(✅|✓|❌|✗|⏳|🔄)/g, '')
        .trim();
      steps.push({ step: stepNum, description: desc, status });
    }
  }

  if (steps.length === 0) return null;

  return { textBefore, textAfter, statusSteps: steps, templateTitle };
}


/**
 * File chips for a message. With `label` set (assistant-generated files) the
 * list renders as a framed panel with an uppercase header + count and
 * pill-style chips; without it (user uploads) the original plain layout is kept.
 */
const AttachmentList: React.FC<{ attachments: FileAttachment[]; label?: string; conversationId?: string }> = ({ attachments, label, conversationId }) => {
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string } | null>(null);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

  const openAttachment = async (att: FileAttachment, i: number) => {
    const fileLink = att.fileUrl || att.preview;
    if (!fileLink) return;
    if (isArtifactViewerUrl(fileLink)) {
      window.open(fileLink, '_blank', 'noopener,noreferrer');
      return;
    }
    // Route through the authenticated file endpoint when this attachment
    // belongs to a saved conversation — the blob SAS URL in `fileUrl` is being
    // phased out. Falls back to it if the endpoint has nothing (e.g. a
    // just-uploaded file not yet persisted server-side).
    if (conversationId && att.name) {
      setLoadingIndex(i);
      const proxied = await fetchConversationFile(conversationId, att.name);
      setLoadingIndex(null);
      setPreviewFile({ name: att.name, url: proxied || fileLink });
      return;
    }
    setPreviewFile({ name: att.name, url: fileLink });
  };

  const list = (
    <div className="chat-msg__attachments">
      {attachments.map((att, i) => {
        const fileLink = att.fileUrl || att.preview;
        const canOpen = !!fileLink;

        return (
          <div key={i} className="chat-msg__attachment">
            <button
              type="button"
              className={`chat-msg__attachment-file${label ? ' chat-msg__attachment-file--chip' : ''}`}
              disabled={!canOpen}
              title={att.name}
              onClick={() => openAttachment(att, i)}
              style={{
                ...(label ? {} : { width: '100%', textAlign: 'left' as const }),
                cursor: canOpen ? 'pointer' : 'default',
                opacity: canOpen ? (loadingIndex === i ? 0.5 : 1) : 0.7,
              }}
            >
              {label && <FiFileText size={13} className="chat-msg__attachment-file-icon" />}
              <span className="chat-msg__attachment-info">
                <span className="chat-msg__attachment-name">{att.name}</span>
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {previewFile && (
        <FilePreviewModal
          name={previewFile.name}
          url={previewFile.url}
          onClose={() => {
            if (previewFile.url.startsWith('blob:')) URL.revokeObjectURL(previewFile.url);
            setPreviewFile(null);
          }}
        />
      )}
      {label ? (
        <div className="chat-msg__artifacts">
          <div className="chat-msg__artifacts-header">
            <FiPaperclip size={11} />
            <span>{label}</span>
            <span className="chat-msg__artifacts-count">{attachments.length}</span>
          </div>
          {list}
        </div>
      ) : (
        list
      )}
    </>
  );
};

const IconZoomIn: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const IconDownload: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconClose: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const AIGeneratedImage: React.FC<{ urls: string[] }> = ({ urls }) => {
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>(() => {
    const init: Record<number, boolean> = {};
    urls.forEach((_, i) => { init[i] = true; });
    return init;
  });
  const [errorStates, setErrorStates] = useState<Record<number, boolean>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const handleLoad = (idx: number) => {
    setLoadingStates(prev => ({ ...prev, [idx]: false }));
  };

  const handleError = (idx: number) => {
    setLoadingStates(prev => ({ ...prev, [idx]: false }));
    setErrorStates(prev => ({ ...prev, [idx]: true }));
  };

  const handleDownload = async (url: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `momentum-ai-image-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <div className="ai-image-gallery">
        {urls.map((url, i) => (
          <div key={i} className="ai-image-wrap">
            {loadingStates[i] && (
              <div className="ai-image-loader">
                <div className="ai-image-loader__spinner" />
                <span className="ai-image-loader__text">Generating image...</span>
              </div>
            )}
            {errorStates[i] ? (
              <div className="ai-image-error">
                <FiAlertTriangle size={20} />
                <span>Failed to load image</span>
              </div>
            ) : (
              <>
                <img
                  src={url}
                  alt="AI generated"
                  className={`ai-image ${loadingStates[i] ? 'ai-image--loading' : 'ai-image--loaded'}`}
                  onLoad={() => handleLoad(i)}
                  onError={() => handleError(i)}
                />
                {!loadingStates[i] && (
                  <div className="ai-image-actions">
                    <button
                      className="ai-image-action-btn"
                      title="View full size"
                      onClick={() => setLightboxUrl(url)}
                    >
                      <IconZoomIn size={14} />
                    </button>
                    <button
                      className="ai-image-action-btn"
                      title="Download image"
                      onClick={() => handleDownload(url)}
                    >
                      <IconDownload size={14} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {lightboxUrl && (
        <div className="ai-lightbox" onClick={() => setLightboxUrl(null)}>
          <div className="ai-lightbox__toolbar" onClick={e => e.stopPropagation()}>
            <button
              className="ai-lightbox__btn"
              title="Download"
              onClick={() => handleDownload(lightboxUrl)}
            >
              <IconDownload size={18} />
            </button>
            <button
              className="ai-lightbox__btn"
              title="Close"
              onClick={() => setLightboxUrl(null)}
            >
              <IconClose size={18} />
            </button>
          </div>
          <img
            src={lightboxUrl}
            alt="AI generated full size"
            className="ai-lightbox__image"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};


interface ChatMessageProps {
  message: ChatMessageType;
  /** The Digital Workforce agent this conversation is with, if any — its
   *  logo/photo (or colour + initials) and name replace the generic AI mark. */
  agent?: { name: string; photo?: string; color: string; initials: string } | null;
  chatId?: string;
  /** Called after the MAC AI widget iframe finishes loading — used to scroll into view */
  onWidgetLoad?: () => void;
  /**
   * When true, hides the like/dislike feedback buttons and inline feedback form.
   * Pass this from contexts that have no feedback API (e.g. MacAIChatPanel).
   * The copy button is always shown regardless.
   */
  disableFeedback?: boolean;
  /**
   * Dynamic status text to display in the thinking indicator instead of the
   * hardcoded "Thinking…". MAC AI only — regular chat leaves this undefined.
   */
  streamingStatusText?: string | null;
  /**
   * True for the most recent message in the thread — auto-opens this message's
   * "View Details" execution panel in the right sidebar, so the last AI turn's
   * tool history is visible without an extra click.
   */
  isLatest?: boolean;
  /**
   * When provided and the message metadata has canGenerateArtifact, shows a
   * "Generate artifact" button in the actions row that fires this callback.
   */
  onGenerateArtifact?: () => void;
  /**
   * True when streaming is still active but chunk delivery is temporarily
   * paused, so we can show a subtle skeleton and reassure users the answer
   * is still coming.
   */
  showStreamingGapIndicator?: boolean;
  /**
   * Sends a reply through the container's normal send path. Pass it only for
   * the latest AI message while the chat is idle; when this message ends with
   * "Do you want me to proceed?" it renders one-click Yes / No replies.
   */
  onQuickReply?: (text: string) => void;
  /**
   * Starts a fresh conversation. Rendered as a "Start new chat" button inside
   * a terminated-session notice (metadata.notice.kind === 'terminated'): the
   * AI session ended on the service side, so the user is asked to begin again.
   */
  onStartNewChat?: () => void;
}

// Reads localStorage fresh on every call (not cached at module load) so a
// logout/login as a different user without a page reload shows the right
// initials instead of the previous user's.
function getUserInitials(): string {
  const f = localStorage.getItem('firstName') ?? '';
  const l = localStorage.getItem('lastName') ?? '';
  const first = f.charAt(0).toUpperCase();
  const last  = l.charAt(0).toUpperCase();
  return first + last || first || (localStorage.getItem('userName') ?? '?').charAt(0).toUpperCase();
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, chatId, onWidgetLoad, disableFeedback, streamingStatusText, isLatest, onGenerateArtifact, showStreamingGapIndicator, onQuickReply , agent, onStartNewChat}) => {
  const { role, content: rawContent, timestamp, metadata, isStreaming } = message;
  // An AI may attach explicit reply buttons as $$SHOWMODAL_<text><Buttons>[A|B]$$.
  // Pull the marker out so everything below (render, copy, charts) sees clean text.
  const { cleaned: content, prompt: quickPrompt } = useMemo(
    () => (role === 'assistant'
      ? parseQuickPrompt(rawContent, !!isStreaming)
      : { cleaned: rawContent, prompt: null }),
    [role, rawContent, isStreaming],
  );
  // The backend decides on the terminal answer event (metadata.proceedPrompt:
  // a prompt, or null for "no buttons"). The marker / "proceed?" heuristics
  // only cover rows with no decision — history rows and older backends.
  const backendDecided = metadata?.proceedPrompt !== undefined;
  const proceedPrompt = backendDecided ? (metadata?.proceedPrompt ?? null) : quickPrompt;
  const { theme } = useTheme();
  const { setStatusTemplate } = useStatusTemplate();
  const { setSelectedInsuredId } = useInsuredDetail();
  const { setSelectedPolicyId } = usePolicyDetail();
  const { setRunDetailPanel } = useRunDetailPanel();
  const isLight = theme === 'light';
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(() => !!metadata?.like);
  const [disliked, setDisliked] = useState(() => !!metadata?.disLike);
  const [feedbackText, setFeedbackText] = useState(() => String((metadata as any)?.feedback ?? ''));
  const [feedbackDraft, setFeedbackDraft] = useState('');
  const [feedbackEditorPos, setFeedbackEditorPos] = useState<{ top: number; left: number } | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const viewDetailsSeqRef = useRef(0);
  const streamingDivRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback for browsers that block clipboard
      const el = document.createElement('textarea');
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [content]);

  const submitFeedbackUpdate = useCallback(async (nextLiked: boolean, nextDisliked: boolean, nextFeedback: string) => {
    const effectiveChatId = message.metadata?.chatId || chatId;
    if (!effectiveChatId) return;
    setIsSubmittingFeedback(true);
    try {
      await submitFeedback({
        ChatId: effectiveChatId,
        liked: nextLiked,
        disliked: nextDisliked,
        feedback: nextFeedback,
      });
      setFeedbackText(nextFeedback);
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [message.metadata?.chatId, chatId]);

  const handleLikeClick = useCallback(() => {
    const newLiked = !liked;
    setLiked(newLiked);
    setDisliked(false);
    void submitFeedbackUpdate(newLiked, false, feedbackText);
  }, [liked, submitFeedbackUpdate, feedbackText]);

  const handleDislikeClick = useCallback(() => {
    const newDisliked = !disliked;
    setDisliked(newDisliked);
    setLiked(false);
    void submitFeedbackUpdate(false, newDisliked, feedbackText);
  }, [disliked, submitFeedbackUpdate, feedbackText]);

  const handleOpenFeedbackEditor = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const popupWidth = 360;
    const popupHeight = 140;
    const padding = 16;
    let top = rect.bottom + 6;
    let left = rect.left;
    if (left + popupWidth > window.innerWidth) left = window.innerWidth - popupWidth - padding;
    if (left < padding) left = padding;
    if (top + popupHeight > window.innerHeight) top = rect.top - popupHeight - 6;
    if (top < padding) top = padding;

    setFeedbackDraft(feedbackText);
    setFeedbackEditorPos({ top, left });
  }, [feedbackText]);

  const handleCloseFeedbackEditor = useCallback(() => {
    setFeedbackEditorPos(null);
  }, []);

  const handleSaveFeedbackEditor = useCallback(() => {
    const nextFeedback = feedbackDraft.trim();
    setFeedbackText(nextFeedback);
    setFeedbackEditorPos(null);
    void submitFeedbackUpdate(liked, disliked, nextFeedback);
  }, [feedbackDraft, liked, disliked, submitFeedbackUpdate]);

  const handleViewDetails = useCallback(async () => {
    const turnChatId = message.metadata?.chatId || chatId;
    if (!turnChatId) return;
    const seq = ++viewDetailsSeqRef.current;
    // Run detail is hidden in SidebarRight whenever an Insured/Policy detail
    // panel is open — clear those first so the run detail panel actually shows.
    setSelectedInsuredId(null);
    setSelectedPolicyId(null);

    // Map before filtering so payload/response indices stay aligned with names;
    // then drop "no tools ran" placeholder names ("[]"/"{}") the backend sends,
    // so the panel shows its proper empty state instead of a fake "[]" step.
    const toExecs = (names: string[], payloads: string[], responses: string[]): RunDetailExecution[] =>
      names.map((name, i) => ({
        tool: name,
        action: payloads[i] ?? '',
        result: responses[i] ?? '',
        status: 'success',
      })).filter(e => !isMeaninglessToolName(e.tool));

    const cachedNames = message.metadata?.aiToolName;
    if (cachedNames && cachedNames.length > 0) {
      setRunDetailPanel({
        run: { id: turnChatId, status: 'done', startedAt: timestamp || new Date().toISOString() },
        execs: toExecs(cachedNames, message.metadata?.aiToolPayload ?? [], message.metadata?.aiToolResponse ?? []),
        exeLoading: false,
      });
      return;
    }

    // If the assistant message body itself is a tool_history JSON payload,
    // use it directly and skip any follow-up history API call.
    const parsedFromContent = extractToolHistoryFromContent(message.content || '');
    if (parsedFromContent) {
      setRunDetailPanel({
        run: { id: turnChatId, status: 'done', startedAt: timestamp || new Date().toISOString() },
        execs: toExecs(parsedFromContent.aiToolName, parsedFromContent.aiToolPayload, parsedFromContent.aiToolResponse),
        exeLoading: false,
      });
      return;
    }

    setRunDetailPanel({
      run: { id: turnChatId, status: 'done', startedAt: timestamp || new Date().toISOString() },
      execs: null,
      exeLoading: true,
    });

    // DW session-history rows commonly do not include per-turn chatId metadata.
    // In that case, fallback history fetch by session id is not turn-accurate,
    // so skip the API call and show an empty details panel instead.
    if (!message.metadata?.chatId || !chatId) {
      if (seq !== viewDetailsSeqRef.current) return;
      setRunDetailPanel({
        run: { id: turnChatId, status: 'done', startedAt: timestamp || new Date().toISOString() },
        execs: [],
        exeLoading: false,
      });
      return;
    }

    const detail = await fetchToolCallDetail(chatId, message.metadata.chatId);
    if (seq !== viewDetailsSeqRef.current) return;
    setRunDetailPanel({
      // The just-fetched turn's own created stamp backs up an unstamped
      // message (e.g. a turn rendered before history carried `created`).
      run: { id: turnChatId, status: 'done', startedAt: timestamp || detail?.createdAt || new Date().toISOString(), notes: detail?.question },
      execs: detail ? toExecs(detail.aiToolName, detail.aiToolPayload, detail.aiToolResponse) : [],
      exeLoading: false,
    });
  }, [message.metadata, chatId, timestamp, setRunDetailPanel, setSelectedInsuredId, setSelectedPolicyId]);

  const canShowDetails = role !== 'user' && !isStreaming && !!(message.metadata?.chatId || chatId);
  // Auto-open this message's execution details in the right sidebar when it's the
  // most recent message in the thread — mirrors clicking "View Details" manually,
  // so the last AI turn's tool history is visible without an extra click.
  useEffect(() => {
    if (isLatest && canShowDetails) handleViewDetails();
  }, [isLatest, canShowDetails, handleViewDetails]);

  // Parse status template once; push to sidebar context when found
  const parsedTemplate = useMemo(() => {
    if (role !== 'assistant' || !content) return null;
    return parseStatusTemplate(content);
  }, [content, role]);

  useEffect(() => {
    if (parsedTemplate) {
      setStatusTemplate({
        statusSteps: parsedTemplate.statusSteps,
        templateTitle: parsedTemplate.templateTitle,
      });
    }
  }, [parsedTemplate, setStatusTemplate]);

  // Processed content — entity links resolved, template markers stripped, and
  // inline artifact links demoted to plain text (files render as a list below).
  const cleanContent = useMemo(() => {
    if (!content || role !== 'assistant') return content || '';
    return stripTemplateMarkers(processEntityLinks(stripArtifactLinks(content)));
  }, [content, role]);

  const contentAttachments = useMemo(
    () => [...extractFilesFromMessageContent(content || ''), ...extractArtifactLinksFromContent(content || '')],
    [content],
  );
  const visibleAttachments = useMemo(() => {
    const merged = [...(metadata?.attachments ?? []), ...contentAttachments];
    const seen = new Set<string>();
    const rows: FileAttachment[] = [];
    for (const file of merged) {
      const key = (file.fileUrl || file.preview || file.name || '').trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      rows.push(file);
    }
    return rows;
  }, [metadata?.attachments, contentAttachments]);
  const displayUserContent = useMemo(() => normalizeAttachedFilesText(content || ''), [content]);

  // Custom link renderer for ReactMarkdown — intercepts insured/policy links
  const markdownComponents = useMemo(() => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    a: ({ href, children, ...props }: any) => {
      if (href?.startsWith('#INSURED_ID_')) {
        const insuredId = href.replace('#INSURED_ID_', '');
        return (
          <button
            className="insured-link"
            onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(insuredId); }}
          >
            {children}
          </button>
        );
      }
      if (href?.startsWith('#POLICY_ID_')) {
        const policyId = href.replace('#POLICY_ID_', '');
        return (
          <button
            className="insured-link insured-link--policy"
            onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(policyId); }}
          >
            {children}
          </button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
  }), [setSelectedInsuredId, setSelectedPolicyId]);

  // Event delegation for streaming divs — intercepts insured/policy anchor clicks
  const handleStreamingClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a[href^="#INSURED_ID_"], a[href^="#POLICY_ID_"]') as HTMLAnchorElement | null;
    if (anchor) {
      e.preventDefault();
      const href = anchor.getAttribute('href')!;
      if (href.startsWith('#INSURED_ID_')) {
        setSelectedPolicyId(null);
        setSelectedInsuredId(href.replace('#INSURED_ID_', ''));
      } else if (href.startsWith('#POLICY_ID_')) {
        setSelectedInsuredId(null);
        setSelectedPolicyId(href.replace('#POLICY_ID_', ''));
      }
    }
  }, [setSelectedInsuredId, setSelectedPolicyId]);

  // Refs for streaming divs — updated directly, bypassing React reconciler
  const streamingBeforeDivRef = useRef<HTMLDivElement>(null);
  const streamingAfterDivRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  // Always hold latest content without triggering re-renders
  const latestCleanContentRef = useRef('');
  latestCleanContentRef.current = cleanContent;

  // RAF-throttled direct DOM update for streaming content.
  // Batches all chunks arriving within a single frame into one innerHTML write,
  // completely bypassing React's reconciler — no DOM churn, no flicker while scrolling.
  useEffect(() => {
    if (!isStreaming) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (streamingDivRef.current) {
        streamingDivRef.current.innerHTML = marked.parse(latestCleanContentRef.current) as string;
      }
      if (streamingBeforeDivRef.current && parsedTemplate?.textBefore) {
        streamingBeforeDivRef.current.innerHTML = marked.parse(
          processEntityLinks(parsedTemplate.textBefore)
        ) as string;
      }
      if (streamingAfterDivRef.current && parsedTemplate?.textAfter) {
        streamingAfterDivRef.current.innerHTML = marked.parse(
          processEntityLinks(parsedTemplate.textAfter)
        ) as string;
      }
    });
    return () => cancelAnimationFrame(rafRef.current);
  }, [isStreaming, cleanContent, parsedTemplate]);

  if (role === 'system') {
    return (
      <motion.div
        className="chat-msg chat-msg--system"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="chat-msg__system-badge">
          <span className="chat-msg__alert-tag">
            <FiAlertTriangle size={10} style={{ marginRight: 4 }} />
            Alert
          </span>
          <span className="chat-msg__system-label">Renewal Detected</span>
          <span className="chat-msg__time">{timestamp}</span>
          <span className="chat-msg__system-type">System / Alert</span>
        </div>
        <p className="chat-msg__system-content">{content}</p>
      </motion.div>
    );
  }

  const isUser = role === 'user';
  const hasThought = !isUser && !!metadata?.thoughtText;
  const isActiveThinking = metadata?.isThinkingNow ?? (!!isStreaming && !content);

  return (
    <motion.div
      className={`chat-msg chat-msg--${role} ${isStreaming ? 'chat-msg--streaming' : ''} ${hasThought ? 'chat-msg--has-thought' : ''}`}
      initial={isStreaming ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={isStreaming ? { duration: 0 } : { duration: 0.35, ease: 'easeOut' }}
    >
      <div className="chat-msg__header">
        <div className="chat-msg__avatar">
          {isUser ? (
            <div className="chat-msg__avatar-user">
              {getUserInitials()}
            </div>
          ) : (
            agent ? (
              <div className="chat-msg__avatar-agent" style={{ background: agent.color }}>
                {agent.photo ? <img src={agent.photo} alt="" /> : agent.initials}
              </div>
            ) : (
              <div className="chat-msg__avatar-ai">
                <img src={aiLogo} alt="AI" className="chat-msg__avatar-ai-img" />
              </div>
            )
          )}
        </div>
        <span className="chat-msg__sender">{isUser ? 'You' : (agent?.name || 'Insurance Agency AI')}</span>
        <span className="chat-msg__time">{timestamp}</span>
        {metadata?.model && (
          <span className="chat-msg__model-badge">
            <RiSparklingFill size={10} style={{ marginRight: 3 }} />
            {metadata.model}
          </span>
        )}
      </div>

      <div className="chat-msg__body">
        {hasThought && (
          <ThinkingBlock
            thoughtText={metadata!.thoughtText!}
            currentTool={metadata?.currentTool}
            currentToolLabel={metadata?.currentToolLabel}
            isActiveThinking={isActiveThinking}
            hasAnswer={!!content}
            planSteps={metadata?.planSteps}
            phase={metadata?.phase}
            stepNumber={metadata?.stepNumber}
            totalSteps={metadata?.totalSteps}
          />
        )}

        {/* User uploads stay above the text; assistant-generated files render
            as a list below the response (end of the message body). */}
        {isUser && visibleAttachments.length > 0 && (
          <AttachmentList attachments={visibleAttachments} conversationId={chatId} />
        )}

        {isUser ? (
          <div>
            <p className="chat-msg__text">{displayUserContent}</p>
            {metadata?.skill && (
              <span className="chat-msg__skill-badge">
                <FiZap size={10} />
                {metadata.skill}
              </span>
            )}
          </div>
        ) : metadata?.widget ? (
          // ── MAC AI Widget branch ───────────────────────────────────────────
          // Checked BEFORE content? because widget messages arrive with content:""
          // and would fall through to null without this branch.
          <div className="chat-msg__mac-widget">
            <MacAIChatWidget widget={metadata.widget} onLoad={onWidgetLoad} />
            {content && (
              <div className="chat-msg__markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {cleanContent}
                </ReactMarkdown>
              </div>
            )}
          </div>
        ) : content ? (
          (() => {
            if (parsedTemplate) {
              // Template sent to sidebar — only render surrounding text in the bubble
              return (
                <>
                  {parsedTemplate.textBefore && (
                    <div className="chat-msg__markdown">
                      {isStreaming ? (
                        // Ref-controlled: innerHTML written by RAF effect, no React DOM churn
                        <div ref={streamingBeforeDivRef} onClick={handleStreamingClick} />
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {processEntityLinks(parsedTemplate.textBefore)}
                        </ReactMarkdown>
                      )}
                    </div>
                  )}
                  {parsedTemplate.textAfter && (
                    <div className="chat-msg__markdown">
                      {isStreaming ? (
                        <div ref={streamingAfterDivRef} onClick={handleStreamingClick} />
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {processEntityLinks(parsedTemplate.textAfter)}
                        </ReactMarkdown>
                      )}
                    </div>
                  )}
                </>
              );
            }

            if (!isStreaming && hasChartBlocks(cleanContent)) {
              const segments = parseChartBlocks(cleanContent);
              return (
                <>
                  {segments.map((seg, idx) =>
                    seg.type === 'chart' && seg.chart ? (
                      <ChartBlock key={`chart-${idx}`} config={seg.chart} />
                    ) : seg.type === 'kpi' && seg.kpiCards ? (
                      <KpiCards key={`kpi-${idx}`} cards={seg.kpiCards} />
                    ) : (
                      <div key={`text-${idx}`} className="chat-msg__markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {seg.content || ''}
                        </ReactMarkdown>
                      </div>
                    )
                  )}
                </>
              );
            }

            return isStreaming ? (
              // Ref-controlled: innerHTML written by RAF effect, no React DOM churn.
              // key="s" forces React to unmount this div when streaming ends so the
              // RAF-written innerHTML is never inherited by the ReactMarkdown div.
              <div
                key="s"
                ref={streamingDivRef}
                className="chat-msg__markdown chat-msg__markdown--streaming"
                onClick={handleStreamingClick}
              />
            ) : (
              // key="f" ensures a fresh DOM node — no leftover RAF innerHTML.
              <div key="f" className="chat-msg__markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {cleanContent}
                </ReactMarkdown>
              </div>
            );
          })()
        ) : isStreaming && !content && !(hasThought && isActiveThinking) ? (
          // No content yet — show thinking indicator while waiting for the first
          // chunk, and keep showing it after ThinkingBlock closes if the model is
          // still composing (otherwise this gap renders nothing and looks stuck).
          // Suppressed while ThinkingBlock is actively showing its own thinking
          // state above — otherwise both render at once ("2 thinking" indicators).
          <div className="thinking-indicator">
            <div className="thinking-indicator__shimmer" />
            <div className="thinking-indicator__text">
              <RiSparklingFill size={11} className="thinking-indicator__icon" />
              <span>{streamingStatusText ?? 'Thinking…'}</span>
            </div>
          </div>
        ) : null}

        {isStreaming && !!content && showStreamingGapIndicator && (
          <div className="chat-msg__stream-gap" aria-live="polite" aria-label="Response is still streaming">
            <div className="chat-msg__stream-gap-head">
              <div className="chat-msg__stream-gap-logo-wrap" aria-hidden="true">
                <span className="chat-msg__stream-gap-ring chat-msg__stream-gap-ring--outer" />
                <span className="chat-msg__stream-gap-ring chat-msg__stream-gap-ring--inner" />
                <img src={aiLogo} alt="" className="chat-msg__stream-gap-logo" />
              </div>
              <div className="chat-msg__stream-gap-meta">
                <span className="chat-msg__stream-gap-title">{agent?.name || 'Insurance Agency AI'} is responding</span>
                <span className="chat-msg__stream-gap-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          </div>
        )}

        {!isStreaming && metadata?.chartJsData && (() => {
          try {
            const chartJs = JSON.parse(metadata.chartJsData);
            if (chartJs.type === 'kpi' && Array.isArray(chartJs.cards)) {
              return <KpiCards cards={chartJs.cards} />;
            }
            const config = convertChartJsToConfig(chartJs);
            if (config) return <ChartBlock config={config} />;
          } catch { /* invalid chart JSON */ }
          return null;
        })()}

        {!isStreaming && metadata?.imageURL && metadata.imageURL.length > 0 && (
          <AIGeneratedImage urls={metadata.imageURL} />
        )}

        {/* Sources section hidden from UI
        {metadata?.pageURL && metadata.pageURL.length > 0 && (
          <div className="chat-msg__sources">
            <span className="chat-msg__sources-label">Sources</span>
            <div className="chat-msg__sources-list">
              {metadata.pageURL.map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chat-msg__source-link"
                >
                  {(() => {
                    try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
                  })()}
                </a>
              ))}
            </div>
          </div>
        )}
        */}

        {metadata?.table && (
          <div className="chat-msg__table-wrap">
            <table className="chat-msg__table">
              <thead>
                <tr>
                  {metadata.table.headers.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metadata.table.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => {
                      const trimmed = cell.trim();
                      const idfMatch = trimmed.match(/^\$\$IDF_([^#$]+)##([^$]+)\$\$$/);
                      const pdfMatch = trimmed.match(/^\$\$PDF_([^#$]+)##([^$]+)\$\$$/);
                      return (
                        <td key={j} className={j === 0 ? 'chat-msg__table-link' : ''}>
                          {idfMatch ? (
                            <button
                              className="insured-link"
                              onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(idfMatch[2].trim()); }}
                            >
                              {idfMatch[1].trim()}
                            </button>
                          ) : pdfMatch ? (
                            <button
                              className="insured-link insured-link--policy"
                              onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(pdfMatch[2].trim()); }}
                            >
                              {pdfMatch[1].trim()}
                            </button>
                          ) : (
                            cell
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isUser && visibleAttachments.length > 0 && (
          <AttachmentList attachments={visibleAttachments} label="Artifacts" />
        )}
      </div>

      {!isUser && !isStreaming && metadata?.notice && (
        // Why the turn ended early: a fatal error, a terminated AI session
        // (the next message starts a fresh one) or a Stop pressed by the user.
        // Rendered as its own banner so the reason is never mistaken for part
        // of the answer, and so an error-only turn still has something to show.
        <div
          className={`chat-msg__notice chat-msg__notice--${metadata.notice.kind}`}
          role={metadata.notice.kind === 'stopped' ? 'status' : 'alert'}
        >
          {metadata.notice.kind === 'stopped' ? <FiSquare size={14} /> : <FiAlertTriangle size={14} />}
          <span className="chat-msg__notice-text">{metadata.notice.text}</span>
          {metadata.notice.kind === 'terminated' && onStartNewChat && (
            <button type="button" className="chat-msg__notice-btn" onClick={onStartNewChat}>
              Start new chat
            </button>
          )}
        </div>
      )}

      {!isUser && !isStreaming && (content || message.metadata?.widget || proceedPrompt) && (
        <>
          {proceedPrompt ? (
            // Backend flag or explicit AI buttons: the question (if any) always
            // shows; buttons only while it is the open prompt.
            <ProceedQuickReplies
              className="chat-msg__proceed"
              text={proceedPrompt.text || undefined}
              options={proceedPrompt.options}
              showButtons={!!onQuickReply}
              onReply={(text) => onQuickReply?.(text)}
            />
          ) : !backendDecided && onQuickReply && asksToProceed(content) && (
            <ProceedQuickReplies className="chat-msg__proceed" onReply={onQuickReply} />
          )}
          <div className="chat-msg__actions">
            <button className={`chat-msg__action-btn ${copied ? 'chat-msg__action-btn--copied' : ''}`} title={copied ? 'Copied!' : 'Copy'} onClick={handleCopy}>
              {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
            </button>
            {(message.metadata?.chatId || chatId) && (
              <button
                className="chat-msg__action-btn chat-msg__action-btn--details"
                title="View execution details"
                onClick={handleViewDetails}
              >
                <FiBarChart2 size={13} /> View Details
              </button>
            )}
            {!disableFeedback && (
              <>
                <span className="chat-msg__action-divider" />
                <div className="chat-msg__feedback-wrap">
                  <button
                    className={`chat-msg__action-btn ${liked ? 'chat-msg__action-btn--liked' : ''}`}
                    title="Like"
                    onClick={handleLikeClick}
                  >
                    <FiThumbsUp size={13} />
                  </button>
                  <button
                    className={`chat-msg__action-btn ${disliked ? 'chat-msg__action-btn--disliked' : ''}`}
                    title="Dislike"
                    onClick={handleDislikeClick}
                  >
                    <FiThumbsDown size={13} />
                  </button>
                  <button
                    className="chat-msg__action-btn"
                    title={feedbackText ? 'Edit feedback' : 'Add feedback'}
                    onClick={handleOpenFeedbackEditor}
                    style={{ color: feedbackText ? '#a78bfa' : undefined }}
                  >
                    <FiEdit3 size={13} />
                  </button>
                </div>
              </>
            )}
            {onGenerateArtifact && metadata?.canGenerateArtifact && (
              <>
                <span className="chat-msg__action-divider" />
                <button
                  className="chat-msg__action-btn chat-msg__action-btn--details"
                  title="Generate a downloadable file from this response"
                  onClick={onGenerateArtifact}
                >
                  <FiPaperclip size={13} /> Generate artifact
                </button>
              </>
            )}
          </div>
        </>
      )}

      {!disableFeedback && (
        <FeedbackEditorPopover
          open={!!feedbackEditorPos}
          position={feedbackEditorPos}
          value={feedbackDraft}
          onChange={setFeedbackDraft}
          onClose={handleCloseFeedbackEditor}
          onSave={handleSaveFeedbackEditor}
          title="Edit Feedback"
          placeholder="Add your feedback here..."
          borderColor={isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)'}
          backgroundColor={isLight ? '#ffffff' : '#1a1a2e'}
          titleColor={isLight ? '#111827' : 'rgba(255,255,255,0.9)'}
          cancelColor={isLight ? '#6b7280' : 'rgba(255,255,255,0.6)'}
          cancelBorderColor={isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)'}
          saveColor="#a78bfa"
          saveBorderColor="rgba(139,92,246,0.4)"
          saveBackgroundColor="rgba(139,92,246,0.16)"
        />
      )}
    </motion.div>
  );
};

export default React.memo(ChatMessage);